import * as React from 'react';

import { VariantProps, cva } from 'class-variance-authority';
import { OTPInput, OTPInputContext } from 'input-otp';

import { cn } from '../lib';

const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(({ className, containerClassName, ...props }, ref) => (
  <OTPInput
    ref={ref}
    containerClassName={cn(
      'flex items-center gap-[10px] has-disabled:opacity-50 flex-wrap',
      containerClassName,
    )}
    className={cn('disabled:cursor-not-allowed', className)}
    {...props}
  />
));
InputOTP.displayName = 'InputOTP';

const InputOTPGroup = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center', className)} {...props} />
));
InputOTPGroup.displayName = 'InputOTPGroup';

const slotVariants = cva(
  'relative flex items-center justify-center text-base font-normal leading-5 transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        default:
          'h-[50px] w-[50px] border-y border-r border-transparent bg-base-500 first:border-l',
        dashboard: 'h-[56px] !w-12 rounded-xl bg-base-300',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const InputOTPSlot = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { index: number } & VariantProps<
      typeof slotVariants
    >
>(({ index, className, variant, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index];

  return (
    <div
      ref={ref}
      className={cn(
        slotVariants({ variant }),
        {
          'z-10': isActive,
          'border-brand-500': isActive && variant === 'default',
          'bg-base-100': isActive && variant === 'dashboard',
        },
        className,
      )}
      style={{ borderRadius: 10 }}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-contrast-500 duration-1000" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = 'InputOTPSlot';

const InputOTPSeparator = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ ...props }, ref) => (
  <div ref={ref} role="separator" {...props}>
    {/* <Dot /> */}
  </div>
));
InputOTPSeparator.displayName = 'InputOTPSeparator';

export { InputOTP, InputOTPGroup, InputOTPSlot };
